import { Box, Step, StepConnector, StepLabel, Stepper, Typography } from "@mui/material";

import { SectionHeader } from "./SectionHeader";
import { TimelineEvent } from "./summaryStatus";

/** Filled dot for events that have happened, hollow outline for pending ones. */
function FlowDot({ completed }: { completed?: boolean }) {
  return (
    <Box
      sx={{
        width: 14,
        height: 14,
        borderRadius: "50%",
        boxSizing: "border-box",
        bgcolor: completed ? "success.main" : "background.paper",
        border: 2,
        borderColor: completed ? "success.main" : "action.disabled",
      }}
    />
  );
}

/**
 * A left-to-right event flow: each event is a node on a connected line, with
 * its caption lines (date, day count, transporter) stacked underneath. Shared
 * by the shipping Timeline and the e-Manifest Upload Compliance timeline.
 */
export function TimelineFlow({ events }: { events: TimelineEvent[] }) {
  return (
    <Stepper
      alternativeLabel
      activeStep={events.filter((e) => e.done).length}
      connector={
        <StepConnector
          // MUI positions alternativeLabel connectors at top: 12px for its
          // default 24px step icons; our 14px dot centers at 7px, so pull the
          // 1px line up to run through the dot centers.
          sx={{ "&.MuiStepConnector-alternativeLabel": { top: 6.5 } }}
        />
      }
    >
      {events.map((event) => (
        <Step key={event.key} completed={event.done}>
          <StepLabel
            StepIconComponent={() => <FlowDot completed={event.done} />}
            optional={
              <Box sx={{ textAlign: "center" }}>
                {event.captionLines.map((line) => (
                  <Typography
                    key={line}
                    variant="caption"
                    color={event.done ? "text.secondary" : "text.disabled"}
                    sx={{ display: "block" }}
                  >
                    {line}
                  </Typography>
                ))}
              </Box>
            }
          >
            <Typography variant="body2" color={event.done ? "text.primary" : "text.disabled"}>
              {event.label}
            </Typography>
          </StepLabel>
        </Step>
      ))}
    </Stepper>
  );
}

interface TimelineSectionProps {
  events: TimelineEvent[];
}

/**
 * The shipping lifecycle as a flow: Shipped → In Transit (per transporter when
 * there are several) → Received by TSDF. Compliance obligations live on the
 * e-Manifest Upload Compliance timeline instead. Source-agnostic.
 */
export function TimelineSection({ events }: TimelineSectionProps) {
  if (events.length === 0) return null;

  return (
    <Box>
      <SectionHeader title="Timeline" />
      <TimelineFlow events={events} />
    </Box>
  );
}
